import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BookOpen, ArrowLeft } from 'lucide-react';
import glossaryTerms from '../data/glossaryTerms';

const GlossaryLetterPage = () => {
  const { letter } = useParams<{ letter: string }>();
  const upperCaseLetter = letter?.toUpperCase() || '';

  const filteredTerms = glossaryTerms.filter(
    (item) => item.term.toUpperCase().startsWith(upperCaseLetter)
  );

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/glossary/${letter}`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Marketing Glossary: Terms Starting with {upperCaseLetter} | Free Resource</title>
        <meta name="description" content={`Explore marketing terms starting with ${upperCaseLetter}. Comprehensive definitions for key marketing concepts and metrics. Free educational resource.`} />
        <meta name="keywords" content={`marketing glossary, ${upperCaseLetter} marketing terms, marketing definitions, marketing concepts, free marketing resource`} />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={`Marketing Glossary: Terms Starting with ${upperCaseLetter} | Free Resource`} />
        <meta property="og:description" content={`Explore marketing terms starting with ${upperCaseLetter}. Comprehensive definitions for key marketing concepts and metrics. Free educational resource.`} />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={`Marketing Glossary: Terms Starting with ${upperCaseLetter} | Free Resource`} />
        <meta property="twitter:description" content={`Explore marketing terms starting with ${upperCaseLetter}. Comprehensive definitions for key marketing concepts and metrics. Free educational resource.`} />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": `Marketing Glossary: Terms Starting with ${upperCaseLetter}`,
            "description": `Explore marketing terms starting with ${upperCaseLetter}. Comprehensive definitions for key marketing concepts and metrics.`,
            "url": pageUrl,
            "image": logoUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": pageUrl
            }
          })}
        </script>
      </Helmet>
      <Link to="/glossary" className="text-indigo-600 hover:text-indigo-800 flex items-center mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Glossary
      </Link>
      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <BookOpen className="w-8 h-8 mr-2 text-indigo-600" />
        Glossary Terms: {upperCaseLetter}
      </h1>

      {filteredTerms.length > 0 ? (
        <div className="bg-white rounded-lg shadow-md p-6">
          <ul className="space-y-4">
            {filteredTerms.map((item, index) => (
              <li key={index}>
                <Link 
                  to={`/glossary-term/${item.term.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-lg font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  {item.term}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-gray-600 text-center">No terms found starting with the letter {upperCaseLetter}.</p>
      )}
    </div>
  );
};

export default GlossaryLetterPage;
